import * as React from 'react';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { AddonsGrid, AddonCard, AddonTitle, AddonDescription } from './styled';
import { SubscriptionAddonDefinition, SubscriptionAddonTier } from '../../models';
import { Switch, Tooltip } from '@mui/material';
import { formatCurrency, getAddonTierPrice } from './utils';

export interface FeatureAddonsProps {
    billingPeriod: 'monthly' | 'yearly';
    addons: Record<string, SubscriptionAddonDefinition>;
    selectedAddons: Record<string, SubscriptionAddonTier | boolean>;
    onAddonChange: (addonType: string, value: SubscriptionAddonTier | boolean | null) => void;
    disabledAddons?: Record<string, string>;
}

const FeatureAddons: React.FC<FeatureAddonsProps> = ({
    billingPeriod,
    addons,
    selectedAddons,
    onAddonChange,
    disabledAddons,
}) => {
    const t = useTranslations();

    const featureAddons = Object.entries(addons).filter(
        ([, addon]) => addon.category?.toLowerCase() === 'feature'
    );

    return (
        <AddonsGrid>
            {featureAddons.map(([type, addon]) => {
                const isSelected = !!selectedAddons[type];
                const selectedTier = selectedAddons[type] && typeof selectedAddons[type] === 'object'
                    ? selectedAddons[type] as SubscriptionAddonTier
                    : null;
                const disabledReason = disabledAddons?.[type];
                const isLockedByPolicy = !!disabledReason;
                const isLockedByMonthlyBilling = billingPeriod === 'monthly' && !addon.availableForMontlhyBilling;
                const isControlDisabled = isLockedByPolicy || isLockedByMonthlyBilling;

                // Simplify description to first sentence
                const shortDescription = addon.description
                    ? `${addon.description.split('.')[0]}.`
                    : t(TK.notAvailable);

                return (
                    <Tooltip
                        key={type}
                        title={disabledReason || ''}
                        disableHoverListener={!isLockedByPolicy}
                        arrow
                    >
                        <AddonCard style={isLockedByPolicy ? { opacity: 0.75 } : undefined}>
                            <div style={{ flex: 1 }}>
                                <AddonTitle>{addon.name}</AddonTitle>
                                <AddonDescription>
                                    {shortDescription}
                                </AddonDescription>
                            </div>

                            {addon.tiers && addon.tiers.length > 1 ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 'auto' }}>
                                    <select
                                        value={selectedTier ? JSON.stringify(selectedTier) : ''}
                                        onChange={(e) => {
                                            if (e.target.value === '') {
                                                onAddonChange(type, null);
                                            } else {
                                                onAddonChange(type, JSON.parse(e.target.value));
                                            }
                                        }}
                                        disabled={isControlDisabled}
                                        style={{
                                            flex: 1,
                                            padding: '8px 10px',
                                            fontSize: '14px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db',
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        <option value="">{t(TK.none)}</option>
                                        {addon.tiers.map((tier, idx) => (
                                            <option key={idx} value={JSON.stringify(tier)}>
                                                {tier.quantity} {addon.type?.toLowerCase() === 'storage' ? t(TK.storageUnit) : ''} - {formatCurrency(getAddonTierPrice(tier, billingPeriod))}
                                            </option>
                                        ))}
                                    </select>
                                    <div style={{ minWidth: '80px', textAlign: 'right', fontWeight: 600, fontSize: '14px' }}>
                                        {selectedTier ? `+${formatCurrency(getAddonTierPrice(selectedTier, billingPeriod))}` : ''}
                                    </div>
                                </div>
                            ) : addon.tiers && addon.tiers.length === 1 ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: 'auto' }}>
                                    <Switch
                                        checked={isSelected}
                                        onChange={() => onAddonChange(type, isSelected ? null : true)}
                                        disabled={isControlDisabled}
                                        color="primary"
                                    />
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: isSelected ? '#0066cc' : '#9ca3af' }}>
                                        {isSelected ? `+${formatCurrency(getAddonTierPrice(addon.tiers[0], billingPeriod))} / ${t(TK.month)}` : `${formatCurrency(getAddonTierPrice(addon.tiers[0], billingPeriod))} / ${t(TK.month)}`}
                                    </div>
                                </div>
                            ) : null}

                            {isLockedByMonthlyBilling && (
                                <div style={{ marginTop: '8px', color: '#dc2626', fontSize: '12px' }}>
                                    {t(TK.addonNotAvailableForMonthlyBilling)}
                                </div>
                            )}
                        </AddonCard>
                    </Tooltip>
                );
            })}
        </AddonsGrid>
    );
};

export default FeatureAddons;
